import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { translations } from "@/i18n";
import { pickLang } from "@/lib/toolbox";
import {
  Lock, LogOut, Home as HomeIcon, Flame, Plus, Trash2, Save, Loader2, Image as ImageIcon, Link as LinkIcon, GripVertical,
  FileText, ExternalLink, ArrowLeft,
} from "lucide-react";
import BbqAdminDashboard from "@/pages/BbqAdminDashboard";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "allrounder_admin_jwt";
const BBQ_KEY = "allrounder_bbq_token";

const LANGS = [
  { code: "ko", label: "KR" },
  { code: "en", label: "EN" },
  { code: "zh", label: "CN" },
  { code: "ja", label: "JP" },
  { code: "es", label: "ES" },
  { code: "pt", label: "PT" },
];
const EMPTY_LANG = { ko: "", en: "", zh: "", ja: "", es: "", pt: "" };

// Default stock images for imported service cards (keyed by i18n item key)
const DEFAULT_IMG = {
  hotel:
    "https://images.unsplash.com/photo-1554009975-d74653b879f1?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTB8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBob3RlbCUyMGFyY2hpdGVjdHVyZSUyMGludGVyaW9yfGVufDB8fHx8MTc3ODg1MzIzMXww&ixlib=rb-4.1.0&q=85",
  study:
    "https://images.unsplash.com/photo-1577985043696-8bd54d9f093f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzJ8MHwxfHNlYXJjaHwxfHx1bml2ZXJzaXR5JTIwc3R1ZGVudHMlMjBzdHVkeWluZyUyMGNhbXB1c3xlbnwwfHx8fDE3Nzg4NTMyMzF8MA&ixlib=rb-4.1.0&q=85",
  wh: "https://images.unsplash.com/photo-1604435490312-36f86cd186dc?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njl8MHwxfHNlYXJjaHwxfHx5b3VuZyUyMHBlb3BsZSUyMHRyYXZlbGluZyUyMGx1Z2dhZ2V8ZW58MHx8fHwxNzc4ODUzMjMxfDA&ixlib=rb-4.1.0&q=85",
  task: "https://images.unsplash.com/photo-1562259929-b4e1fd3aef09?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAxODF8MHwxfHNlYXJjaHwxfHxoYW5keW1hbiUyMHdvcmtpbmclMjBtb2Rlcm58ZW58MHx8fHwxNzc4ODUzMjMxfDA&ixlib=rb-4.1.0&q=85",
};

function authHeaders() {
  return { Authorization: `Bearer ${localStorage.getItem(TOKEN_KEY) || ""}` };
}

// ---------- Login ----------
export function LoginScreen({ onLogin }) {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setErr("");
    try {
      const { data } = await axios.post(`${API}/admin/login`, { username, password });
      localStorage.setItem(TOKEN_KEY, data.token);
      localStorage.setItem(BBQ_KEY, data.bbq_token || "");
      onLogin(data);
    } catch (e2) {
      const d = e2?.response?.data?.detail;
      setErr(typeof d === "string" ? d : "로그인에 실패했습니다.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0E1A] flex items-center justify-center px-5">
      <form
        onSubmit={submit}
        data-testid="admin-login-form"
        className="w-full max-w-sm rounded-2xl bg-[#111827] border border-white/10 p-8 shadow-2xl"
      >
        <div className="w-12 h-12 rounded-xl bg-[#0A369D] flex items-center justify-center mb-5">
          <Lock className="w-5 h-5 text-white" />
        </div>
        <h1 className="text-white text-2xl font-black tracking-tight" style={{ fontFamily: "'Fraunces', serif" }}>
          Admin Login
        </h1>
        <p className="text-white/50 text-sm mt-1">All Rounder 관리자</p>

        <label className="block mt-6 text-[11px] uppercase tracking-widest font-bold text-white/50">아이디</label>
        <input
          data-testid="admin-login-username"
          value={username}
          onChange={(e) => setUsername(e.target.value)}
          className="mt-1.5 w-full h-11 rounded-lg bg-[#0A0E1A] border border-white/10 px-3 text-white focus:outline-none focus:ring-2 focus:ring-[#0A369D]"
          autoComplete="username"
        />
        <label className="block mt-4 text-[11px] uppercase tracking-widest font-bold text-white/50">비밀번호</label>
        <input
          data-testid="admin-login-password"
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          className="mt-1.5 w-full h-11 rounded-lg bg-[#0A0E1A] border border-white/10 px-3 text-white focus:outline-none focus:ring-2 focus:ring-[#0A369D]"
          autoComplete="current-password"
        />
        {err ? <p className="mt-3 text-red-400 text-sm" data-testid="admin-login-error">{err}</p> : null}
        <button
          type="submit"
          disabled={loading}
          data-testid="admin-login-submit"
          className="mt-6 w-full h-11 rounded-lg bg-[#0A369D] hover:bg-[#08297A] text-white font-bold flex items-center justify-center gap-2 disabled:opacity-50"
        >
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Lock className="w-4 h-4" />} 로그인
        </button>
      </form>
    </div>
  );
}

// ---------- Toolbox editor row ----------
function ToolboxEditor({ box, onChange, onSave, onDelete, saving }) {
  const [lang, setLang] = useState("ko");

  const setField = (field, value) => onChange({ ...box, [field]: value });
  const setLangField = (field, value) =>
    onChange({ ...box, [field]: { ...(box[field] || {}), [lang]: value } });

  const uploadImage = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    if (!f.type.startsWith("image/")) return toast.error("이미지 파일만 가능합니다");
    if (f.size > 10_000_000) return toast.error("10MB 이하로 올려주세요");
    const dataUrl = await new Promise((res, rej) => {
      const r = new FileReader();
      r.onload = () => res(r.result);
      r.onerror = rej;
      r.readAsDataURL(f);
    });
    setField("image", dataUrl);
  };

  const isBbq = box.variant === "bbq";

  return (
    <div
      data-testid={`toolbox-editor-${box.id || box._tmp}`}
      className={`rounded-xl border p-5 space-y-4 ${isBbq ? "border-[#E85A2C]/40 bg-[#E85A2C]/5" : "border-white/10 bg-[#0F172A]"}`}
    >
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <GripVertical className="w-4 h-4 text-white/30" />
          <span className={`text-[10px] uppercase tracking-widest font-bold px-2 py-1 rounded-full ${isBbq ? "bg-[#E85A2C] text-white" : "bg-[#0A369D] text-white"}`}>
            {isBbq ? "BBQ / 이벤트 카드" : "서비스 카드"}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => onSave(box)}
            disabled={saving}
            data-testid={`toolbox-save-${box.id || box._tmp}`}
            className="text-[11px] uppercase tracking-widest font-bold px-3 py-2 rounded-md bg-emerald-600 hover:bg-emerald-500 text-white flex items-center gap-1.5 disabled:opacity-50"
          >
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />} 저장
          </button>
          <button
            onClick={() => onDelete(box)}
            data-testid={`toolbox-delete-${box.id || box._tmp}`}
            className="text-[11px] uppercase tracking-widest font-bold px-3 py-2 rounded-md border border-red-500/40 text-red-400 hover:bg-red-500/10 flex items-center gap-1.5"
          >
            <Trash2 className="w-3.5 h-3.5" /> 삭제
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-[120px_1fr] gap-4">
        {/* Image */}
        <div>
          <div className="aspect-square rounded-lg overflow-hidden border border-white/10 bg-black/30 flex items-center justify-center">
            {box.image ? (
              <img src={box.image} alt="" className="w-full h-full object-cover" />
            ) : (
              <ImageIcon className="w-6 h-6 text-white/20" />
            )}
          </div>
          <label className="mt-2 block text-center cursor-pointer text-[10px] uppercase tracking-widest font-bold px-2 py-1.5 rounded-md bg-white/10 text-white hover:bg-white/20">
            사진 업로드
            <input type="file" accept="image/*" className="hidden" onChange={uploadImage} data-testid={`toolbox-image-${box.id || box._tmp}`} />
          </label>
        </div>

        {/* Settings */}
        <div className="space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] uppercase tracking-widest font-bold text-white/40 flex items-center gap-1">
                <LinkIcon className="w-3 h-3" /> 링크 (클릭 시 이동)
              </label>
              <input
                value={box.link || ""}
                onChange={(e) => setField("link", e.target.value)}
                placeholder="/bbq 또는 https://..."
                data-testid={`toolbox-link-${box.id || box._tmp}`}
                className="mt-1 w-full h-9 rounded-md bg-[#0A0E1A] border border-white/10 px-2.5 text-white text-sm focus:outline-none focus:ring-2 focus:ring-[#0A369D]"
              />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest font-bold text-white/40">스타일</label>
              <select
                value={box.variant || "standard"}
                onChange={(e) => setField("variant", e.target.value)}
                data-testid={`toolbox-variant-${box.id || box._tmp}`}
                className="mt-1 w-full h-9 rounded-md bg-[#0A0E1A] border border-white/10 px-2.5 text-white text-sm focus:outline-none focus:ring-2 focus:ring-[#0A369D]"
              >
                <option value="standard">일반 (파랑)</option>
                <option value="bbq">이벤트 강조 (오렌지)</option>
              </select>
            </div>
          </div>

          {/* Language tabs */}
          <div className="flex items-center gap-1 flex-wrap">
            {LANGS.map((l) => (
              <button
                key={l.code}
                onClick={() => setLang(l.code)}
                data-testid={`toolbox-lang-${box.id || box._tmp}-${l.code}`}
                className={`text-[10px] font-bold px-2.5 py-1 rounded-full transition-colors ${
                  lang === l.code ? "bg-[#0A369D] text-white" : "bg-white/5 text-white/50 hover:text-white"
                }`}
              >
                {l.label}
              </button>
            ))}
          </div>

          <div className="space-y-2">
            <input
              value={(box.tag || {})[lang] || ""}
              onChange={(e) => setLangField("tag", e.target.value)}
              placeholder="태그 (예: ACCOMMODATION)"
              data-testid={`toolbox-tag-${box.id || box._tmp}`}
              className="w-full h-9 rounded-md bg-[#0A0E1A] border border-white/10 px-2.5 text-white text-sm focus:outline-none focus:ring-2 focus:ring-[#0A369D]"
            />
            <input
              value={(box.title || {})[lang] || ""}
              onChange={(e) => setLangField("title", e.target.value)}
              placeholder="제목"
              data-testid={`toolbox-title-${box.id || box._tmp}`}
              className="w-full h-9 rounded-md bg-[#0A0E1A] border border-white/10 px-2.5 text-white text-sm focus:outline-none focus:ring-2 focus:ring-[#0A369D]"
            />
            <textarea
              value={(box.desc || {})[lang] || ""}
              onChange={(e) => setLangField("desc", e.target.value)}
              placeholder="설명"
              rows={2}
              data-testid={`toolbox-desc-${box.id || box._tmp}`}
              className="w-full rounded-md bg-[#0A0E1A] border border-white/10 px-2.5 py-2 text-white text-sm resize-none focus:outline-none focus:ring-2 focus:ring-[#0A369D]"
            />
          </div>

          {/* Hover banner (마우스 올리면 뜨는 배너) */}
          <div className="pt-2 border-t border-white/5 space-y-2">
            <label className="text-[10px] uppercase tracking-widest font-bold text-[#E85A2C]">
              마우스 오버 배너 ({lang.toUpperCase()})
            </label>
            <textarea
              value={((box.banner || {}).text || {})[lang] || ""}
              onChange={(e) => onChange({ ...box, banner: { ...(box.banner || {}), text: { ...((box.banner || {}).text || {}), [lang]: e.target.value } } })}
              placeholder={"마우스 올리면 뜨는 문구 (여러 줄 가능)"}
              rows={2}
              data-testid={`toolbox-banner-text-${box.id || box._tmp}`}
              className="w-full rounded-md bg-[#0A0E1A] border border-white/10 px-2.5 py-2 text-white text-sm resize-none focus:outline-none focus:ring-2 focus:ring-[#E85A2C]"
            />
            <input
              value={(box.banner || {}).url || ""}
              onChange={(e) => onChange({ ...box, banner: { ...(box.banner || {}), url: e.target.value } })}
              placeholder="배너에 표시할 사이트 주소 (예: https://...)"
              data-testid={`toolbox-banner-url-${box.id || box._tmp}`}
              className="w-full h-9 rounded-md bg-[#0A0E1A] border border-white/10 px-2.5 text-white text-sm focus:outline-none focus:ring-2 focus:ring-[#E85A2C]"
            />
            <p className="text-white/30 text-[10px]">
              {isBbq ? "이벤트 카드는 마우스 오버 시 BBQ 갤러리 사진이 자동으로 스르륵 표시됩니다. 배너 문구는 그 위에 함께 뜹니다." : "문구는 6개 언어별로 입력하세요. URL은 모든 언어 공통입니다."}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

// ---------- Home CMS ----------
function HomeCms() {
  const [hero, setHero] = useState("");
  const [savingHero, setSavingHero] = useState(false);
  const [boxes, setBoxes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [h, t] = await Promise.all([
        axios.get(`${API}/site-hero`),
        axios.get(`${API}/site-toolboxes`),
      ]);
      setHero(h.data?.hero_image || "");
      setBoxes(t.data.items || []);
    } catch {
      toast.error("불러오기 실패");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const uploadHero = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    if (f.size > 10_000_000) return toast.error("10MB 이하로 올려주세요");
    const dataUrl = await new Promise((res, rej) => {
      const r = new FileReader();
      r.onload = () => res(r.result);
      r.onerror = rej;
      r.readAsDataURL(f);
    });
    setHero(dataUrl);
  };

  const saveHero = async () => {
    setSavingHero(true);
    try {
      await axios.put(`${API}/admin/site-hero`, { hero_image: hero }, { headers: authHeaders() });
      toast.success("메인 이미지 저장됨");
    } catch { toast.error("저장 실패"); } finally { setSavingHero(false); }
  };

  const changeBox = (idx, next) => setBoxes((arr) => arr.map((b, i) => (i === idx ? next : b)));

  const saveBox = async (box) => {
    setSavingId(box.id || box._tmp);
    const body = { image: box.image || "", link: box.link || "", variant: box.variant || "standard", tag: box.tag || {}, title: box.title || {}, desc: box.desc || {}, banner: box.banner || {} };
    try {
      if (box.id) {
        await axios.put(`${API}/admin/site-toolboxes/${box.id}`, body, { headers: authHeaders() });
        toast.success("툴박스 저장됨");
      } else {
        const { data } = await axios.post(`${API}/admin/site-toolboxes`, body, { headers: authHeaders() });
        toast.success("툴박스 추가됨");
        setBoxes((arr) => arr.map((b) => (b._tmp === box._tmp ? { ...b, id: data.id } : b)));
      }
    } catch { toast.error("저장 실패"); } finally { setSavingId(null); }
  };

  const deleteBox = async (box) => {
    if (box.id) {
      try { await axios.delete(`${API}/admin/site-toolboxes/${box.id}`, { headers: authHeaders() }); } catch { return toast.error("삭제 실패"); }
    }
    setBoxes((arr) => arr.filter((b) => (box.id ? b.id !== box.id : b._tmp !== box._tmp)));
    toast.success("삭제됨");
  };

  const addBox = (variant = "standard") =>
    setBoxes((arr) => [...arr, { _tmp: `tmp-${Date.now()}`, variant, image: "", link: variant === "bbq" ? "/bbq" : "", tag: { ...EMPTY_LANG }, title: { ...EMPTY_LANG }, desc: { ...EMPTY_LANG } }]);

  const importDefaults = async () => {
    const items = translations.ko.services.items;
    const created = [];
    for (let i = 0; i < items.length; i++) {
      const key = items[i].key;
      const isBbq = key === "bbq";
      const tag = {}, title = {}, desc = {};
      LANGS.forEach((l) => {
        const it = translations[l.code]?.services?.items?.[i] || {};
        tag[l.code] = it.tag || "";
        title[l.code] = it.title || "";
        desc[l.code] = it.desc || "";
      });
      const body = { image: DEFAULT_IMG[key] || "", link: isBbq ? "/bbq" : "#inquiry", variant: isBbq ? "bbq" : "standard", tag, title, desc };
      try {
        const { data } = await axios.post(`${API}/admin/site-toolboxes`, body, { headers: authHeaders() });
        created.push({ ...body, id: data.id });
      } catch { /* ignore */ }
    }
    toast.success(`기본 툴박스 ${created.length}개 불러옴`);
    load();
  };

  if (loading) {
    return <div className="py-20 text-center text-white/40"><Loader2 className="w-6 h-6 animate-spin inline" /></div>;
  }

  return (
    <div className="space-y-8" data-testid="home-cms">
      {/* Hero */}
      <section className="rounded-xl border border-white/10 bg-[#0F172A] p-5 sm:p-6">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <div className="text-[10px] uppercase tracking-[0.28em] font-bold text-[#0A369D]">Main Hero Image</div>
            <div className="text-white/50 text-[11px] mt-1">메인 홈 맨 위 큰 이미지 · 최대 10MB</div>
          </div>
          <div className="flex items-center gap-2">
            <label className="cursor-pointer text-[11px] uppercase tracking-widest font-bold px-3 py-2 rounded-md bg-white/10 text-white hover:bg-white/20">
              사진 선택
              <input type="file" accept="image/*" className="hidden" onChange={uploadHero} data-testid="home-hero-upload" />
            </label>
            <button onClick={saveHero} disabled={savingHero} data-testid="home-hero-save" className="text-[11px] uppercase tracking-widest font-bold px-3 py-2 rounded-md bg-[#0A369D] hover:bg-[#08297A] text-white flex items-center gap-1.5 disabled:opacity-50">
              {savingHero ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />} 저장
            </button>
          </div>
        </div>
        <div className="mt-4 aspect-[16/7] max-w-xl rounded-lg overflow-hidden border border-white/10 bg-black/30 flex items-center justify-center">
          {hero ? <img src={hero} alt="" className="w-full h-full object-cover" /> : <span className="text-white/30 text-xs">기본 브리즈번 이미지 사용 중</span>}
        </div>
      </section>

      {/* Toolboxes */}
      <section className="space-y-4">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <div className="text-[10px] uppercase tracking-[0.28em] font-bold text-[#0A369D]">Tool Boxes · 서비스/이벤트 카드</div>
            <div className="text-white/50 text-[11px] mt-1">추가·삭제·수정 가능 · 각 카드는 6개 언어로 입력 · 링크 설정 시 클릭하면 이동</div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {boxes.length === 0 && (
              <button onClick={importDefaults} data-testid="cms-import-defaults" className="text-[11px] uppercase tracking-widest font-bold px-3 py-2 rounded-md bg-white/10 text-white hover:bg-white/20">
                기본 툴박스 불러오기
              </button>
            )}
            <button onClick={() => addBox("standard")} data-testid="cms-add-standard" className="text-[11px] uppercase tracking-widest font-bold px-3 py-2 rounded-md bg-[#0A369D] hover:bg-[#08297A] text-white flex items-center gap-1.5">
              <Plus className="w-3.5 h-3.5" /> 서비스 카드
            </button>
            <button onClick={() => addBox("bbq")} data-testid="cms-add-bbq" className="text-[11px] uppercase tracking-widest font-bold px-3 py-2 rounded-md bg-[#E85A2C] hover:bg-[#f26a3c] text-white flex items-center gap-1.5">
              <Flame className="w-3.5 h-3.5" /> 이벤트 카드
            </button>
          </div>
        </div>

        {boxes.length === 0 ? (
          <div className="py-12 text-center text-white/40 border border-dashed border-white/10 rounded-xl">
            아직 툴박스가 없습니다. "기본 툴박스 불러오기"로 현재 홈 카드를 가져오거나 새로 추가하세요.<br />
            <span className="text-white/30 text-xs">(툴박스가 없으면 홈에는 현재 기본 카드가 표시됩니다.)</span>
          </div>
        ) : (
          <div className="space-y-4">
            {boxes.map((box, idx) => (
              <ToolboxEditor
                key={box.id || box._tmp}
                box={box}
                onChange={(next) => changeBox(idx, next)}
                onSave={saveBox}
                onDelete={deleteBox}
                saving={savingId === (box.id || box._tmp)}
              />
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

// ---------- Page builder (custom pages with slugs) ----------
function fileToDataUrl(f) {
  return new Promise((res, rej) => {
    const r = new FileReader();
    r.onload = () => res(r.result);
    r.onerror = rej;
    r.readAsDataURL(f);
  });
}

const blankPage = () => ({
  slug: "",
  published: true,
  title: { ...EMPTY_LANG },
  subtitle: { ...EMPTY_LANG },
  hero_image: "",
  blocks: [],
});

function PageEditor({ page, onChange, onSave, onDelete, onClose, saving }) {
  const [lang, setLang] = useState("ko");
  const set = (field, v) => onChange({ ...page, [field]: v });
  const setLangField = (field, v) => onChange({ ...page, [field]: { ...(page[field] || {}), [lang]: v } });

  const uploadHero = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    if (f.size > 10_000_000) return toast.error("10MB 이하로 올려주세요");
    set("hero_image", await fileToDataUrl(f));
  };

  const changeBlock = (idx, next) => set("blocks", page.blocks.map((b, i) => (i === idx ? next : b)));
  const addBlock = () => set("blocks", [...(page.blocks || []), { _tmp: `b-${Date.now()}`, image: "", heading: { ...EMPTY_LANG }, text: { ...EMPTY_LANG } }]);
  const removeBlock = (idx) => set("blocks", page.blocks.filter((_, i) => i !== idx));
  const uploadBlockImg = async (idx, e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    if (f.size > 10_000_000) return toast.error("10MB 이하로 올려주세요");
    changeBlock(idx, { ...page.blocks[idx], image: await fileToDataUrl(f) });
  };

  return (
    <div className="rounded-xl border border-white/10 bg-[#0F172A] p-5 sm:p-6 space-y-5" data-testid="page-editor">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <button onClick={onClose} className="text-[11px] uppercase tracking-widest font-bold text-white/50 hover:text-white flex items-center gap-1.5">
          <ArrowLeft className="w-3.5 h-3.5" /> 목록
        </button>
        <div className="flex items-center gap-2">
          {page.id && page.published && (
            <a href={`/${page.slug}`} target="_blank" rel="noreferrer" className="text-[11px] uppercase tracking-widest font-bold px-3 py-2 rounded-md bg-white/10 text-white hover:bg-white/20 flex items-center gap-1.5">
              <ExternalLink className="w-3.5 h-3.5" /> 보기
            </a>
          )}
          <button onClick={() => onSave(page)} disabled={saving} data-testid="page-save" className="text-[11px] uppercase tracking-widest font-bold px-3 py-2 rounded-md bg-emerald-600 hover:bg-emerald-500 text-white flex items-center gap-1.5 disabled:opacity-50">
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />} 저장
          </button>
          {page.id && (
            <button onClick={() => onDelete(page)} data-testid="page-delete" className="text-[11px] uppercase tracking-widest font-bold px-3 py-2 rounded-md border border-red-500/40 text-red-400 hover:bg-red-500/10 flex items-center gap-1.5">
              <Trash2 className="w-3.5 h-3.5" /> 삭제
            </button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto] gap-3 items-end">
        <div>
          <label className="text-[10px] uppercase tracking-widest font-bold text-white/40">페이지 주소 (슬러그)</label>
          <div className="mt-1 flex items-center gap-2">
            <span className="text-white/40 text-sm">allrounderound.com/</span>
            <input
              value={page.slug}
              onChange={(e) => set("slug", e.target.value)}
              placeholder="study"
              data-testid="page-slug"
              className="flex-1 h-9 rounded-md bg-[#0A0E1A] border border-white/10 px-2.5 text-white text-sm focus:outline-none focus:ring-2 focus:ring-[#0A369D]"
            />
          </div>
        </div>
        <label className="flex items-center gap-2 text-white/70 text-sm h-9">
          <input type="checkbox" checked={page.published} onChange={(e) => set("published", e.target.checked)} data-testid="page-published" className="w-4 h-4 accent-[#0A369D]" />
          공개
        </label>
      </div>

      {/* Language tabs */}
      <div className="flex items-center gap-1 flex-wrap">
        {LANGS.map((l) => (
          <button key={l.code} onClick={() => setLang(l.code)} data-testid={`page-lang-${l.code}`}
            className={`text-[10px] font-bold px-2.5 py-1 rounded-full transition-colors ${lang === l.code ? "bg-[#0A369D] text-white" : "bg-white/5 text-white/50 hover:text-white"}`}>
            {l.label}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-[140px_1fr] gap-4">
        <div>
          <div className="aspect-video rounded-lg overflow-hidden border border-white/10 bg-black/30 flex items-center justify-center">
            {page.hero_image ? <img src={page.hero_image} alt="" className="w-full h-full object-cover" /> : <ImageIcon className="w-6 h-6 text-white/20" />}
          </div>
          <label className="mt-2 block text-center cursor-pointer text-[10px] uppercase tracking-widest font-bold px-2 py-1.5 rounded-md bg-white/10 text-white hover:bg-white/20">
            메인 이미지
            <input type="file" accept="image/*" className="hidden" onChange={uploadHero} data-testid="page-hero-upload" />
          </label>
        </div>
        <div className="space-y-2">
          <input value={(page.title || {})[lang] || ""} onChange={(e) => setLangField("title", e.target.value)} placeholder="페이지 제목" data-testid="page-title"
            className="w-full h-10 rounded-md bg-[#0A0E1A] border border-white/10 px-3 text-white focus:outline-none focus:ring-2 focus:ring-[#0A369D]" />
          <input value={(page.subtitle || {})[lang] || ""} onChange={(e) => setLangField("subtitle", e.target.value)} placeholder="부제 (선택)" data-testid="page-subtitle"
            className="w-full h-9 rounded-md bg-[#0A0E1A] border border-white/10 px-3 text-white text-sm focus:outline-none focus:ring-2 focus:ring-[#0A369D]" />
        </div>
      </div>

      {/* Blocks */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <span className="text-[10px] uppercase tracking-widest font-bold text-white/40">내용 블록</span>
          <button onClick={addBlock} data-testid="page-add-block" className="text-[11px] uppercase tracking-widest font-bold px-3 py-1.5 rounded-md bg-white/10 text-white hover:bg-white/20 flex items-center gap-1.5">
            <Plus className="w-3.5 h-3.5" /> 블록 추가
          </button>
        </div>
        {(page.blocks || []).map((b, idx) => (
          <div key={b.id || b._tmp} data-testid={`page-block-editor-${idx}`} className="rounded-lg border border-white/10 bg-[#0A0E1A] p-4 grid grid-cols-1 sm:grid-cols-[120px_1fr] gap-3">
            <div>
              <div className="aspect-square rounded-md overflow-hidden border border-white/10 bg-black/30 flex items-center justify-center">
                {b.image ? <img src={b.image} alt="" className="w-full h-full object-cover" /> : <ImageIcon className="w-5 h-5 text-white/20" />}
              </div>
              <label className="mt-1.5 block text-center cursor-pointer text-[9px] uppercase tracking-widest font-bold px-2 py-1 rounded bg-white/10 text-white hover:bg-white/20">
                이미지
                <input type="file" accept="image/*" className="hidden" onChange={(e) => uploadBlockImg(idx, e)} />
              </label>
            </div>
            <div className="space-y-2">
              <input value={(b.heading || {})[lang] || ""} onChange={(e) => changeBlock(idx, { ...b, heading: { ...(b.heading || {}), [lang]: e.target.value } })} placeholder="소제목"
                className="w-full h-9 rounded-md bg-[#111827] border border-white/10 px-2.5 text-white text-sm focus:outline-none focus:ring-2 focus:ring-[#0A369D]" />
              <textarea value={(b.text || {})[lang] || ""} onChange={(e) => changeBlock(idx, { ...b, text: { ...(b.text || {}), [lang]: e.target.value } })} placeholder="본문" rows={3}
                className="w-full rounded-md bg-[#111827] border border-white/10 px-2.5 py-2 text-white text-sm resize-none focus:outline-none focus:ring-2 focus:ring-[#0A369D]" />
              <button onClick={() => removeBlock(idx)} className="text-[10px] uppercase tracking-widest font-bold text-red-400 hover:text-red-300 flex items-center gap-1">
                <Trash2 className="w-3 h-3" /> 블록 삭제
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export function PagesCms() {
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/pages`, { headers: authHeaders() });
      setPages(data.items || []);
    } catch { toast.error("불러오기 실패"); } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const savePage = async (page) => {
    setSaving(true);
    const body = {
      slug: page.slug, published: !!page.published, title: page.title || {}, subtitle: page.subtitle || {},
      hero_image: page.hero_image || "",
      blocks: (page.blocks || []).map((b) => ({ id: b.id, image: b.image || "", heading: b.heading || {}, text: b.text || {} })),
    };
    try {
      if (page.id) {
        const { data } = await axios.put(`${API}/admin/pages/${page.id}`, body, { headers: authHeaders() });
        toast.success("페이지 저장됨");
        setSelected({ ...page, slug: data.slug });
      } else {
        const { data } = await axios.post(`${API}/admin/pages`, body, { headers: authHeaders() });
        toast.success(`페이지 생성됨 · /${data.slug}`);
        setSelected({ ...page, id: data.id, slug: data.slug });
      }
      load();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "저장 실패");
    } finally { setSaving(false); }
  };

  const deletePage = async (page) => {
    if (page.id) {
      try { await axios.delete(`${API}/admin/pages/${page.id}`, { headers: authHeaders() }); } catch { return toast.error("삭제 실패"); }
    }
    toast.success("삭제됨");
    setSelected(null);
    load();
  };

  if (loading) return <div className="py-20 text-center text-white/40"><Loader2 className="w-6 h-6 animate-spin inline" /></div>;

  if (selected) {
    return <PageEditor page={selected} onChange={setSelected} onSave={savePage} onDelete={deletePage} onClose={() => setSelected(null)} saving={saving} />;
  }

  return (
    <div className="space-y-5" data-testid="pages-cms">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-[0.28em] font-bold text-[#0A369D]">Pages · 페이지</div>
          <div className="text-white/50 text-[11px] mt-1">바베큐(/bbq)처럼 심플한 주소로 새 페이지를 만듭니다. 홈 툴박스 링크에 <b>/주소</b>를 넣으면 연결됩니다.</div>
        </div>
        <button onClick={() => setSelected(blankPage())} data-testid="pages-new" className="text-[11px] uppercase tracking-widest font-bold px-3 py-2 rounded-md bg-[#0A369D] hover:bg-[#08297A] text-white flex items-center gap-1.5">
          <Plus className="w-3.5 h-3.5" /> 새 페이지
        </button>
      </div>

      {pages.length === 0 ? (
        <div className="py-12 text-center text-white/40 border border-dashed border-white/10 rounded-xl">
          아직 만든 페이지가 없습니다. "새 페이지"로 시작하세요.
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {pages.map((p) => (
            <button key={p.id} onClick={() => setSelected(p)} data-testid={`page-row-${p.slug}`}
              className="text-left rounded-xl border border-white/10 bg-[#0F172A] hover:border-[#0A369D]/50 p-4 transition-colors">
              <div className="flex items-center justify-between gap-2">
                <span className="text-white font-bold">{pickLang(p.title, "ko") || "(제목 없음)"}</span>
                <span className={`text-[9px] uppercase tracking-widest font-bold px-2 py-0.5 rounded-full ${p.published ? "bg-emerald-500/15 text-emerald-400" : "bg-white/10 text-white/50"}`}>
                  {p.published ? "공개" : "비공개"}
                </span>
              </div>
              <div className="mt-1 text-[#0A369D] text-sm font-mono flex items-center gap-1"><FileText className="w-3.5 h-3.5" /> /{p.slug}</div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ---------- Unified admin shell ----------
export default function AdminApp() {
  const [authed, setAuthed] = useState(!!localStorage.getItem(TOKEN_KEY));
  const [tab, setTab] = useState("home"); // home | bbq
  const bbqToken = localStorage.getItem(BBQ_KEY) || "";

  // Validate token on mount
  useEffect(() => {
    if (!localStorage.getItem(TOKEN_KEY)) return;
    axios.get(`${API}/admin/me`, { headers: authHeaders() })
      .then((r) => { localStorage.setItem(BBQ_KEY, r.data.bbq_token || ""); setAuthed(true); })
      .catch(() => { localStorage.removeItem(TOKEN_KEY); setAuthed(false); });
  }, []);

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(BBQ_KEY);
    setAuthed(false);
  };

  if (!authed) return <LoginScreen onLogin={() => setAuthed(true)} />;

  return (
    <div className="min-h-screen bg-[#0A0E1A]">
      <header className="sticky top-0 z-40 bg-[#0A0E1A]/90 backdrop-blur border-b border-white/10">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-14 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <span className="text-white font-black tracking-tight" style={{ fontFamily: "'Fraunces', serif" }}>All Rounder Admin</span>
          </div>
          <div className="flex items-center gap-1.5">
            <button
              onClick={() => setTab("home")}
              data-testid="admin-tab-home"
              className={`text-[11px] uppercase tracking-widest font-bold px-3 py-2 rounded-md flex items-center gap-1.5 ${tab === "home" ? "bg-[#0A369D] text-white" : "text-white/50 hover:text-white"}`}
            >
              <HomeIcon className="w-3.5 h-3.5" /> 메인 홈
            </button>
            <button
              onClick={() => setTab("pages")}
              data-testid="admin-tab-pages"
              className={`text-[11px] uppercase tracking-widest font-bold px-3 py-2 rounded-md flex items-center gap-1.5 ${tab === "pages" ? "bg-[#0A369D] text-white" : "text-white/50 hover:text-white"}`}
            >
              <FileText className="w-3.5 h-3.5" /> 페이지
            </button>
            <button
              onClick={() => setTab("bbq")}
              data-testid="admin-tab-bbq"
              className={`text-[11px] uppercase tracking-widest font-bold px-3 py-2 rounded-md flex items-center gap-1.5 ${tab === "bbq" ? "bg-[#E85A2C] text-white" : "text-white/50 hover:text-white"}`}
            >
              <Flame className="w-3.5 h-3.5" /> BBQ
            </button>
            <button onClick={logout} data-testid="admin-logout" className="ml-2 text-[11px] uppercase tracking-widest font-bold px-3 py-2 rounded-md border border-white/10 text-white/60 hover:text-white flex items-center gap-1.5">
              <LogOut className="w-3.5 h-3.5" /> 로그아웃
            </button>
          </div>
        </div>
      </header>

      {tab === "home" ? (
        <main className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
          <HomeCms />
        </main>
      ) : tab === "pages" ? (
        <main className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
          <PagesCms />
        </main>
      ) : (
        <div data-testid="admin-bbq-embed">
          <BbqAdminDashboard tokenProp={bbqToken} />
        </div>
      )}
    </div>
  );
}
